import React, { useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Plus, Edit2, Trash2, ExternalLink, Upload, X, Image, Loader } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    original_price: '',
    discounted_price: '',
    product_link: '',
    tracking_link: '',
    image_url: '',
    images: []
  });

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProduct) {
        await axios.put(
          `${API_URL}/admin/products/${editingProduct.product_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Produto atualizado!');
      } else {
        await axios.post(
          `${API_URL}/admin/products`,
          formData,
          { withCredentials: true }
        );
        toast.success('Produto criado!');
      }
      
      setIsDialogOpen(false);
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        original_price: '',
        discounted_price: '',
        product_link: '',
        tracking_link: '',
        image_url: '',
        images: []
      });
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao salvar produto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      original_price: product.original_price,
      discounted_price: product.discounted_price,
      product_link: product.product_link,
      tracking_link: product.tracking_link || '',
      image_url: product.image_url || '',
      images: product.images || []
    });
    setIsDialogOpen(true);
  };

  // Verifica se é produto de vendedor (admin não pode editar imagens)
  const isSellerProduct = editingProduct?.seller_id ? true : false;

  const handleDelete = async (productId) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/products/${productId}`, { withCredentials: true });
      toast.success('Produto excluído!');
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  // Upload de imagem única
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem válida');
      return;
    }

    // Validar tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 10MB');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/upload/product-image`,
        formDataUpload,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      const imageUrl = `${API_URL}/files/${response.data.file_id}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Imagem enviada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  // Upload de múltiplas imagens para galeria
  const handleMultipleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validar
    const validFiles = files.filter(f => {
      if (!f.type.startsWith('image/')) {
        toast.error(`${f.name}: não é uma imagem válida`);
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name}: muito grande (máx 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;
    if (validFiles.length > 10) {
      toast.error('Máximo 10 imagens por vez');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    validFiles.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(
        `${API_URL}/upload/multiple`,
        formDataUpload,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.uploaded?.length > 0) {
        const newUrls = response.data.uploaded.map(f => `${API_URL}/files/${f.file_id}`);
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newUrls]
        }));
        toast.success(`${response.data.uploaded.length} imagem(ns) enviada(s)!`);
      }

      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => {
          toast.error(`${err.file}: ${err.error}`);
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar imagens');
    } finally {
      setUploading(false);
    }
  };

  // Remover imagem da galeria
  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="admin-products-title">
              Gerenciar Produtos
            </h1>
            <p className="text-lg text-gray-600">Adicione, edite ou remova produtos</p>
          </div>
          <Button
            onClick={() => {
              setEditingProduct(null);
              setFormData({
                name: '',
                description: '',
                original_price: '',
                discounted_price: '',
                product_link: '',
                tracking_link: '',
                image_url: '',
                images: []
              });
              setIsDialogOpen(true);
            }}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Produto
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-lg text-gray-600">Nenhum produto cadastrado</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div
                key={product.product_id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`product-item-${product.product_id}`}
              >
                {product.image_url && (
                  <div className="aspect-video w-full overflow-hidden bg-gray-100">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{product.description}</p>
                  
                  <div className="space-y-1 mb-4">
                    <div className="flex items-baseline space-x-2">
                      <span className="text-sm text-gray-500 line-through">R$ {product.original_price.toFixed(2)}</span>
                      <span className="text-lg font-bold text-indigo-600">R$ {product.discounted_price.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(product)}
                      className="flex-1"
                      data-testid={`edit-product-${product.product_id}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(product.product_id)}
                      data-testid={`delete-product-${product.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="product-dialog-title">
              {editingProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nome do Produto</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="product-name-input"
              />
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                required
                data-testid="product-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preço Original (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.original_price}
                  onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                  required
                  data-testid="product-original-price-input"
                />
              </div>
              <div>
                <Label>Preço com Desconto (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.discounted_price}
                  onChange={(e) => setFormData({ ...formData, discounted_price: e.target.value })}
                  required
                  data-testid="product-discounted-price-input"
                />
              </div>
            </div>

            <div>
              <Label>Link do Produto</Label>
              <Input
                type="url"
                value={formData.product_link}
                onChange={(e) => setFormData({ ...formData, product_link: e.target.value })}
                required
                data-testid="product-link-input"
              />
            </div>

            <div>
              <Label>Link de Rastreio (Opcional)</Label>
              <Input
                type="url"
                value={formData.tracking_link}
                onChange={(e) => setFormData({ ...formData, tracking_link: e.target.value })}
                data-testid="product-tracking-input"
              />
            </div>

            {/* Imagens - Apenas para produtos do Admin (não de vendedores) */}
            {isSellerProduct ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Image className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 text-sm">Imagens gerenciadas pelo vendedor</p>
                    <p className="text-xs text-amber-700 mt-1">
                      As imagens deste produto são controladas pelo vendedor. 
                      Admin pode moderar o status do produto, mas não suas imagens.
                    </p>
                    {formData.images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {formData.images.map((url, index) => (
                          <div key={index} className="w-16 h-16 rounded-lg overflow-hidden border border-amber-200">
                            <img
                              src={url}
                              alt={`Imagem ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label>Imagem Principal</Label>
                  <div className="space-y-3">
                    {/* Preview da imagem principal */}
                    {formData.image_url && (
                      <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-gray-200">
                        <img
                          src={formData.image_url}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    
                    {/* Upload ou URL */}
                    <div className="flex gap-2">
                      <label className="flex-1">
                        <div className="flex items-center justify-center h-10 px-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-colors">
                          {uploading ? (
                            <Loader className="w-5 h-5 animate-spin text-indigo-600" />
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2 text-gray-500" />
                              <span className="text-sm text-gray-600">Upload Imagem</span>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                          disabled={uploading}
                          data-testid="product-image-upload"
                        />
                      </label>
                      <span className="flex items-center text-sm text-gray-400">ou</span>
                      <Input
                        type="url"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        data-testid="product-image-input"
                        placeholder="Cole a URL da imagem"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Galeria de Imagens</Label>
                  <div className="space-y-3">
                    {/* Preview da galeria */}
                    {formData.images.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.images.map((url, index) => (
                          <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                            <img
                              src={url}
                              alt={`Galeria ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(index)}
                              className="absolute top-0.5 right-0.5 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upload múltiplo */}
                    <label className="block">
                      <div className="flex items-center justify-center h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-colors">
                        {uploading ? (
                          <Loader className="w-5 h-5 animate-spin text-indigo-600" />
                        ) : (
                          <div className="text-center">
                            <Image className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                            <span className="text-sm text-gray-600">
                              Clique para adicionar imagens à galeria
                            </span>
                            <p className="text-xs text-gray-400">Máx. 10 imagens por vez (10MB cada)</p>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleMultipleImageUpload}
                        disabled={uploading}
                        data-testid="product-gallery-upload"
                      />
                    </label>
                    
                    <p className="text-xs text-gray-500">
                      {formData.images.length > 0 
                        ? `${formData.images.length} imagem(ns) na galeria`
                        : 'Nenhuma imagem na galeria'}
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1" disabled={uploading} data-testid="product-submit-btn">
                {editingProduct ? 'Atualizar' : 'Criar'} Produto
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                data-testid="product-cancel-btn"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};